import { toNumber } from "lodash";
import Teachr from "../../../Objects/models/Teachr";
import Functions from "../../../Helpers/Functions";
import { coachIsSubjectToVat, coachShareTtc, coachVatIncluded } from "../../../Utils/CoachVatAmounts";
import {
    VT_MONTHLY_CEILING_TTC,
    isCooperativeCoach,
    isVerifiedTrainer,
    vtClientShare,
    vtCoachShareHt,
    vtCoachVat,
    vtCommissions,
    vtHtFromTtc,
    vtIsSubjectToVat,
    vtTerritorialVatRate,
    vtTtcFromHourlyPaid,
    vtTtcFromNet,
} from "../../../Utils/VerifiedTrainerAmounts";

/**
 * Montants d'une déclaration, copiés de ThirdStep et LastStep de l'ancien
 * parcours, regroupés en un seul bloc (recommandation de l'audit : « vous
 * recevez », « le client paie », « reste à sa charge »).
 *
 * Teach'r et Déclarons : le prof reçoit prix horaire × 2 × (1 − frais) par
 * heure (25 € → 43,75 € avec 12,5 % de frais), TVA comprise s'il est assujetti.
 * Le client paie le prix total TTC (prix × 2 × heures) dont la moitié est
 * prise en charge par l'avance immédiate.
 * Verified Trainer et coopérative : part HT du coach au taux du territoire du
 * client, TVA ajoutée par-dessus s'il est assujetti.
 */
export interface DeclarationAmounts {
    price: number;
    hours: number;
    coop: boolean;
    territorialRate: number;
    /** Prix total TTC, ce que paie le client au total (devis). */
    totalTtc: number;
    /** Part prise en charge par l'avance immédiate (la moitié). */
    advance: number;
    deposit: number;
    /** Reste à payer pour le client après avance immédiate et acompte. */
    remaining: number;
    /** Ce que reçoit le prof, TTC s'il est assujetti. */
    received: number;
    receivedHourly: number;
    receivedHt: number;
    receivedVat: number;
    vatSubject: boolean;
    /** Frais de gestion (Teach'r, Déclarons) ou commissions (coopérative). */
    fees: number;
    feesRate: number;
    /** Prix total hors TVA du territoire (coopérative). */
    totalHt: number;
}

export const computeAmounts = (
    priceInput: string | number | null | undefined,
    hours: number,
    teachr: Teachr | null | undefined,
    child: any,
    depositInput: string | number | null | undefined = 0
): DeclarationAmounts => {
    const price = Math.max(0, toNumber(priceInput) || 0);
    const deposit = Math.max(0, toNumber(depositInput) || 0);
    const rates = teachr as any;
    const coop = isCooperativeCoach(rates);
    const territorialRate = vtTerritorialVatRate(child?.zip);
    const feesRate = toNumber(rates?.defaultFees) || 0;
    const totalTtc = vtTtcFromHourlyPaid(price, hours);
    const advance = vtClientShare(totalTtc);
    const remaining = Math.max(0, advance - deposit);

    if (coop) {
        const receivedHt = vtCoachShareHt(totalTtc, rates, territorialRate);
        const receivedVat = vtCoachVat(totalTtc, rates, territorialRate);
        return {
            price, hours, coop, territorialRate, totalTtc, advance, deposit, remaining,
            received: receivedHt + receivedVat,
            receivedHourly: hours > 0 ? (receivedHt + receivedVat) / hours : 0,
            receivedHt, receivedVat,
            vatSubject: vtIsSubjectToVat(rates),
            fees: vtCommissions(totalTtc, rates, territorialRate),
            feesRate,
            totalHt: vtHtFromTtc(totalTtc, territorialRate),
        };
    }
    const received = coachShareTtc(price, hours, rates);
    const receivedVat = coachVatIncluded(received, rates);
    return {
        price, hours, coop, territorialRate, totalTtc, advance, deposit, remaining,
        received,
        receivedHourly: coachShareTtc(price, 1, rates),
        receivedHt: received - receivedVat,
        receivedVat,
        vatSubject: coachIsSubjectToVat(rates),
        fees: totalTtc * feesRate,
        feesRate,
        totalHt: totalTtc,
    };
};

/**
 * Montant de cette déclaration tel qu'il est compté dans le plafond mensuel
 * (même calcul que ThirdStep : part nette pour Teach'r et Déclarons, après la
 * TVA du prof ; prix total TTC pour Verified Trainer).
 */
export const declarationNetAmount = (priceInput: string | number | null | undefined, hours: number, teachr: Teachr | null | undefined): number => {
    const price = toNumber(priceInput) || 0;
    const rates = teachr as any;
    if (isVerifiedTrainer()) return vtTtcFromHourlyPaid(price, hours);
    const tva = Number(rates?.tauxTva);
    const adjusted = tva === 10 ? price * 0.9 : tva === 20 ? price * 0.8 : price;
    const invoiced = adjusted * 2 * hours;
    const netMultiplier = 1 - (toNumber(rates?.defaultFees) || 0);
    return isNaN(invoiced) ? 0 : invoiced * netMultiplier;
};

/** Moitié du montant facturé, plafond de l'acompte (même règle que ThirdStep). */
export const depositCeiling = (priceInput: string | number | null | undefined, hours: number, teachr: Teachr | null | undefined): number => {
    const price = toNumber(priceInput) || 0;
    const tva = Number((teachr as any)?.tauxTva);
    const adjusted = tva === 10 ? price * 0.9 : tva === 20 ? price * 0.8 : price;
    return (adjusted * 2 * hours) / 2;
};

export interface MonthlyCeiling {
    declared: number;
    thisDeclaration: number;
    total: number;
    ceiling: number;
    exceeds: boolean;
    /** Parts (0 à 1) pour la barre de progression. */
    declaredRatio: number;
    thisRatio: number;
}

/** Plafond du mois : déjà déclaré + cette déclaration, comparé au centime (comme ThirdStep). */
export const monthlyCeiling = (
    priceInput: string | number | null | undefined,
    hours: number,
    teachr: Teachr | null | undefined,
    child: any,
    appliesToCurrentMonth: boolean
): MonthlyCeiling => {
    const rates = teachr as any;
    const isVt = isVerifiedTrainer();
    const netMultiplier = 1 - (toNumber(rates?.defaultFees) || 0);
    const declared = isVt ? vtTtcFromNet(child?.amountForTheMonth, rates) : parseFloat(child?.amountForTheMonth ?? "0") || 0;
    const ceiling = isVt ? VT_MONTHLY_CEILING_TTC : 1980 * netMultiplier;
    const thisDeclaration = declarationNetAmount(priceInput, hours, teachr);
    const total = declared + thisDeclaration;
    const exceeds = appliesToCurrentMonth && thisDeclaration > 0 && Math.round(total * 100) / 100 > Math.round(ceiling * 100) / 100;
    return {
        declared,
        thisDeclaration,
        total,
        ceiling,
        exceeds,
        declaredRatio: ceiling > 0 ? Math.min(1, declared / ceiling) : 0,
        thisRatio: ceiling > 0 ? Math.max(0, Math.min(1 - Math.min(1, declared / ceiling), thisDeclaration / ceiling)) : 0,
    };
};

/** Prix total TTC d'une heure au tarif saisi (ex. 50 € pour 25 € saisis). */
export const hourlyTtc = (priceInput: string | number | null | undefined): number => Functions.priceTotalTTC(toNumber(priceInput) || 0);
